import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

import Splash from "@/pages/Splash";
import Consent from "@/pages/Consent";
import Home from "@/pages/Home";
import NewComplaint from "@/pages/NewComplaint";
import ComplaintDetail from "@/pages/ComplaintDetail";
import Regulator from "@/pages/Regulator";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import Wins from "@/pages/Wins";
import Landing from "@/pages/Landing";
import PwaInstall from "@/components/PwaInstall";

function Gate({ children }) {
    const { loading, consumer, needsConsent } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-[var(--speakup-text-muted)]">
                Loading SpeakUp…
            </div>
        );
    }
    if (!consumer) return <Navigate to="/login" replace />;
    if (needsConsent) return <Navigate to="/consent" replace />;
    return children;
}

function AuthGate({ children }) {
    const { loading, consumer, needsConsent } = useAuth();
    if (loading) return null;
    if (consumer && !needsConsent) return <Navigate to="/app" replace />;
    if (consumer && needsConsent) return <Navigate to="/consent" replace />;
    return children;
}

/** Smart root: marketing Landing for guests, Home (My Complaints) for authed users. */
function RootEntry() {
    const { loading, consumer, needsConsent } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-[var(--speakup-text-muted)]">
                Loading SpeakUp…
            </div>
        );
    }
    if (!consumer) return <Landing />;
    if (needsConsent) return <Navigate to="/consent" replace />;
    return <Home />;
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<RootEntry />} />
                    <Route path="/landing" element={<Landing />} />
                    <Route path="/login" element={<AuthGate><Splash /></AuthGate>} />
                    <Route path="/auth" element={<Navigate to="/login" replace />} />
                    <Route path="/consent" element={<Consent />} />
                    <Route path="/app" element={<Gate><Home /></Gate>} />
                    <Route path="/new" element={<Gate><NewComplaint /></Gate>} />
                    <Route path="/c/:id" element={<Gate><ComplaintDetail /></Gate>} />
                    <Route path="/c/:id/regulator" element={<Gate><Regulator /></Gate>} />
                    <Route path="/account" element={<Gate><Account /></Gate>} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/wins" element={<Wins />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
            <Toaster richColors position="top-center" />
            <PwaInstall />
        </AuthProvider>
    );
}
